import React from 'react'
import type { PresentationData } from '../../types/presentation'
import {
  EnhancedTitleSlide,
  EnhancedGridSlide,
  EnhancedContentSlide,
  FeatureCard,
  InfoBox,
} from '../../components/presentation'
import { IconSolid, PromptModal } from '../../components/presentation'
import contextEngineeringPrompts from './contextEngineeringPrompts'

/**
 * Context Engineering - Professional Foundations (English)
 * 
 * Systematic AI development foundations for enterprise environments
 */

export const contextEngineeringProfessionalEn: PresentationData = {
  metadata: {
    title: 'Context Engineering - Professional Foundations',
    description: 'Systematic methodology for efficient AI development',
    author: 'Jan Pfrommer',
    date: 'October 2025',
    tags: ['Context Engineering', 'AI Development', 'Enterprise', 'Systematic'],
  },
  slides: [
    // ============================================
    // SLIDE 1: Title
    // ============================================
    {
      id: 1,
      component: () => (
        <EnhancedTitleSlide
          title="CONTEXT ENGINEERING"
          subtitle="Professional Foundations for Efficient AI Development"
          author="Jan Pfrommer"
          date="October 2025"
          tags={['🎯 Systematic Approach', '📈 Measurable Results', '⚡ Production Ready']}
          background="primary"
          footer={
            <div className="text-center text-lg font-light text-white/90">
              From Basic Prompts to Enterprise AI Workflows
            </div>
          }
        />
      ),
      transition: 'fade',
    },

    // ============================================
    // SLIDE 2: About Me
    // ============================================
    {
      id: 2,
      component: () => (
        <EnhancedGridSlide
          title="About Me"
          subtitle="Expertise in Context Engineering & Enterprise AI"
          columns={2}
          background="secondary"
          items={[
            <div className="flex flex-col items-center text-center">
              <div className="w-48 h-48 rounded-full overflow-hidden bg-gradient-to-br from-primary-600 to-secondary-600 mb-6 flex items-center justify-center shadow-2xl">
                <img src="/src/assets/jan.png" alt="Jan Pfrommer" className="w-full h-full object-cover" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-primary-600 dark:text-primary-400">Jan Pfrommer</h3>
              <div className="text-lg space-y-2 text-gray-700 dark:text-gray-300">
                <p>• Software Engineer</p>
                <p>• Context Engineering Specialist</p>
                <p>• Enterprise AI Solutions Expert</p>
              </div>
            </div>,
            <div className="space-y-6">
              <FeatureCard
                icon={<IconSolid name="graduation-cap" size="xl" className="text-primary-600" />}
                title="6+ Years Software Engineering"
                description="Comprehensive experience in enterprise development"
              />
              <FeatureCard
                icon={<IconSolid name="brain" size="xl" className="text-secondary-600" />}
                title="AI & Context Engineering"
                description="Specialized in systematic AI integration"
              />
              <FeatureCard
                icon={<IconSolid name="building" size="xl" className="text-accent-600" />}
                title="Enterprise Solutions"
                description="Production-ready AI systems"
              />
            </div>,
          ]}
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 3: What is Context Engineering?
    // ============================================
    {
      id: 3,
      component: () => (
        <EnhancedContentSlide
          title="What is Context Engineering?"
          subtitle="The systematic methodology for optimal AI interaction"
          background="light"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FeatureCard
              icon={<IconSolid name="network-wired" size="lg" className="text-primary-600" />}
              title="Structured Communication"
              description="with AI systems"
              items={[
                'Precise task definition',
                'Contextual information',
                'Expectation management'
              ]}
            />
            <FeatureCard
              icon={<IconSolid name="sync-alt" size="lg" className="text-secondary-600" />}
              title="Reproducible Workflows"
              description="for consistent results"
              items={[
                'Repeatable processes', 
                'Standardized approaches',
                'Quality assurance'
              ]}
            />
            <FeatureCard
              icon={<IconSolid name="project-diagram" size="lg" className="text-accent-600" />}
              title="Context Management"
              description="for complex projects"
              items={[
                'Information architecture',
                'Manage dependencies',
                'Scalable structures'
              ]}
            />
            <FeatureCard
              icon={<IconSolid name="shield-alt" size="lg" className="text-green-600" />}
              title="Enterprise Integration"
              description="into existing systems"
              items={[
                'API integration',
                'Security standards',
                'Compliance requirements'
              ]}
            />
          </div>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 4: Business Impact
    // ============================================
    {
      id: 4,
      component: () => (
        <EnhancedGridSlide
          title="Business Impact"
          subtitle="Why Context Engineering is essential"
          columns={2}
          background="primary"
          items={[
            <FeatureCard
              icon={<IconSolid name="times-circle" size="2xl" className="text-red-400" />}
              title="❌ Ad-hoc AI Usage"
              description="Unpredictable, costly results"
              items={[
                'Unpredictable results',
                'High time investment for corrections',
                'Inconsistent code quality',
                'Team frustration',
                'Hard to scale processes',
                'No quality control'
              ]}
              variant="glass"
            />,
            <FeatureCard
              icon={<IconSolid name="check-circle" size="2xl" className="text-green-400" />}
              title="✅ Systematic Context Engineering"
              description="Predictable, efficient, scalable"
              items={[
                'Consistent, high-quality outputs',
                'First iteration often optimal',
                'Enterprise-grade standards',
                'Efficient, predictable workflow',
                'Team-compatible methods',
                'Measurable quality improvement'
              ]}
              variant="glass"
            />,
          ]}
          footer={
            <InfoBox variant="success">
              <strong>ROI:</strong> Companies report 3-5x productivity gains through systematic Context Engineering
            </InfoBox>
          }
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 5: The 5 Pillars
    // ============================================
    {
      id: 5,
      component: () => (
        <EnhancedGridSlide
          title="The 5 Pillars of Context Engineering"
          subtitle="Fundamental principles for systematic success"
          columns={3}
          background="secondary"
          items={[
            <FeatureCard
              icon={<IconSolid name="bullseye" size="2xl" className="text-primary-600" />}
              title="1. Clarity"
              description="Precise goal definition"
              items={[
                'Clear task requirements',
                'Measurable success',
                'Defined scope'
              ]}
              variant="elevated"
            />,
            <FeatureCard
              icon={<IconSolid name="sitemap" size="2xl" className="text-secondary-600" />}
              title="2. Structure"
              description="Systematic construction"
              items={[
                'Logical information hierarchy',
                'Standardized formats',
                'Reusable patterns'
              ]}
              variant="elevated"
            />,
            <FeatureCard
              icon={<IconSolid name="database" size="2xl" className="text-accent-600" />}
              title="3. Context"
              description="Relevant information management"
              items={[
                'Technical constraints',
                'Business requirements',
                'Historical context'
              ]}
              variant="elevated"
            />,
            <FeatureCard
              icon={<IconSolid name="sync-alt" size="2xl" className="text-green-600" />}
              title="4. Iteration"
              description="Continuous optimization"
              items={[
                'Feedback loops',
                'Incremental improvements',
                'Learning from results'
              ]}
              variant="elevated"
            />,
            <FeatureCard
              icon={<IconSolid name="chart-line" size="2xl" className="text-blue-600" />}
              title="5. Measurement"
              description="Quantifiable success control"
              items={[
                'Define KPIs',
                'Quality metrics',
                'Performance tracking'
              ]}
              variant="elevated"
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 6: Enterprise Prompt Framework
    // ============================================
    {
      id: 6,
      component: () => (
        <EnhancedContentSlide
          title="Enterprise Prompt Framework"
          subtitle="Structured approach for professional AI communication"
          background="light"
        >
          <div className="bg-slate-900 text-cyan-400 p-6 rounded-lg font-mono text-sm mb-6 overflow-x-auto">
            <div className="space-y-4">
              <div>
                <span className="text-green-400"># ROLE DEFINITION</span><br/>
                <span className="text-white">You are a Senior Software Architect with 15+ years enterprise experience<br/>
                Specialization: Microservices, Cloud Architecture, Security Best Practices</span>
              </div>
              <div>
                <span className="text-green-400"># BUSINESS CONTEXT</span><br/>
                <span className="text-white">Project: Financial Services Platform Migration<br/>
                Current: Legacy Monolith (Java/Spring) → Cloud-Native Architecture<br/>
                Constraints: GDPR Compliance, 99.9% SLA, PCI-DSS Level 1</span>
              </div>
              <div>
                <span className="text-green-400"># TECHNICAL REQUIREMENTS</span><br/>
                <span className="text-white">Stack: Kubernetes, Service Mesh, Event-Driven Architecture<br/>
                Database: PostgreSQL clusters, Redis caching, Elasticsearch<br/>
                Monitoring: Prometheus, Grafana, Distributed Tracing</span>
              </div>
              <div>
                <span className="text-green-400"># DELIVERABLE SPECIFICATION</span><br/>
                <span className="text-white">Output: Migration strategy document including:<br/>
                - Phase-by-phase decomposition plan<br/>
                - Risk assessment with mitigation strategies<br/>
                - Performance benchmarks and testing approach</span>
              </div>
            </div>
          </div>

          <InfoBox variant="tip">
            <strong>Framework Benefits:</strong> Structured prompts achieve 80-90% success rate on first attempt
          </InfoBox>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 7: Prompt Comparison
    // ============================================
    {
      id: 7,
      component: () => (
        <EnhancedGridSlide
          title="Professional Examples"
          subtitle="From poor to enterprise-grade prompts"
          columns={2}
          background="primary"
          items={[
            <div className="space-y-6">
              <FeatureCard
                icon={<IconSolid name="times-circle" size="xl" className="text-red-400" />}
                title="❌ Unprofessional Prompt"
                description="Vague, ambiguous, without context"
                variant="glass"
              />
              <div className="bg-red-900/30 border border-red-600/50 p-4 rounded-lg">
                <code className="text-red-200 text-sm">
                  "Make my website faster and add some security stuff. Also fix bugs."
                </code>
              </div>
              <ul className="text-red-200 space-y-1 text-sm">
                <li>• Vague requirements</li>
                <li>• No measurable goals</li>
                <li>• Missing technical context</li>
                <li>• Unprofessional scope</li>
              </ul>
            </div>,
            <div className="space-y-6">
              <FeatureCard
                icon={<IconSolid name="check-circle" size="xl" className="text-green-400" />}
                title="✅ Enterprise-Grade Prompt"
                description="Specific, structured, with context"
                variant="glass"
              />
              <div className="bg-green-900/30 border border-green-600/50 p-4 rounded-lg">
                <code className="text-green-200 text-xs">
                  "Optimize React SPA performance for e-commerce:<br/>
                  • Current: 4.2s LCP, 250ms FID<br/>
                  • Target: &lt;2.5s LCP, &lt;100ms FID<br/>  
                  • Tech: React 18, Webpack 5, CDN<br/>
                  • Constraints: SEO compatibility<br/>
                  • Metrics: Core Web Vitals, Lighthouse"
                </code>
              </div>
              <ul className="text-green-200 space-y-1 text-sm">
                <li>• Specific metrics</li>
                <li>• Clear technical context</li>
                <li>• Measurable success criteria</li>
                <li>• Professional deliverables</li>
              </ul>
            </div>,
          ]}
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 8: Systematic Framework
    // ============================================
    {
      id: 8,
      component: () => (
        <EnhancedContentSlide
          title="Systematic Context Framework"
          subtitle="The 4-step process for successful AI interaction"
          background="light"
        >
          <div className="flex justify-center items-center gap-4 mb-8 flex-wrap">
            <FeatureCard
              icon={<IconSolid name="bullseye" size="xl" className="text-primary-600" />}
              title="1. Business Goal"
              description="What business value?"
              variant="elevated"
            />
            <IconSolid name="arrow-right" size="xl" className="text-gray-400 hidden md:block" />
            <FeatureCard
              icon={<IconSolid name="cogs" size="xl" className="text-secondary-600" />}
              title="2. Technical Context"
              description="Stack, constraints, architecture"
              variant="elevated"
            />
            <IconSolid name="arrow-right" size="xl" className="text-gray-400 hidden md:block" />
            <FeatureCard
              icon={<IconSolid name="clipboard-list" size="xl" className="text-accent-600" />}
              title="3. Requirements"
              description="Functional & non-functional"
              variant="elevated"
            />
            <IconSolid name="arrow-right" size="xl" className="text-gray-400 hidden md:block" />
            <FeatureCard
              icon={<IconSolid name="chart-line" size="xl" className="text-green-600" />}
              title="4. Success Metrics"
              description="Measurable outcomes"
              variant="elevated"
            />
          </div>

          <InfoBox variant="tip">
            <h4 className="font-bold mb-3">🎯 Enterprise Best Practices:</h4>
            <ul className="space-y-2 text-sm">
              <li>• Start with business value, not technical features</li>
              <li>• Include compliance and security requirements upfront</li>
              <li>• Define measurable success criteria before implementation</li>
              <li>• Specify rollback and monitoring strategies</li>
              <li>• Document assumptions and dependencies explicitly</li>
            </ul>
          </InfoBox>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 9: Anti-Patterns
    // ============================================
    {
      id: 9,
      component: () => (
        <EnhancedGridSlide
          title="Enterprise Anti-Patterns to Avoid"
          subtitle="Common mistakes and how to avoid them"
          columns={2}
          background="secondary"
          items={[
            <FeatureCard
              icon={<IconSolid name="exclamation-triangle" size="xl" className="text-red-500" />}
              title="Scope Creep Prompts"
              description="Over-ambitious tasks"
              items={[
                'Problem: "Build a complete CRM system with AI, blockchain, and microservices"',
                'Solution: Break task into smaller, specific steps',
                'Best Practice: Use MVP approach'
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="ban" size="xl" className="text-red-500" />}
              title="Missing Constraints"
              description="No clear boundaries"
              items={[
                'Problem: No security standards, compliance rules, or coding guidelines specified',
                'Solution: Define security requirements, compliance needs, and code standards upfront',
                'Best Practice: Include GDPR, accessibility, coding conventions in context'
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="question-circle" size="xl" className="text-red-500" />}
              title="Unmeasurable Goals"
              description="Vague success criteria"
              items={[
                'Problem: "Make it better, faster, more scalable"',
                'Solution: Concrete metrics and benchmarks',
                'Best Practice: Apply SMART criteria'
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="random" size="xl" className="text-red-500" />}
              title="Context Switching"
              description="Sudden direction changes"
              items={[
                'Problem: Requirement changes without transition',
                'Solution: Communicate explicit context switches',
                'Best Practice: Incremental refinement'
              ]}
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 10: Tools & Methods
    // ============================================
    {
      id: 10,
      component: () => (
        <EnhancedGridSlide
          title="Enterprise Tooling & Methods"
          subtitle="Professional tools for systematic AI usage"
          columns={3}
          background="light"
          items={[
            <FeatureCard
              icon={<IconSolid name="user-tie" size="xl" className="text-primary-600" />}
              title="Role-Based Prompting"
              description="Define specific expert perspectives"
              items={[
                'Senior Architect role',
                'Domain expert perspective',
                'Stakeholder-specific views'
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="book" size="xl" className="text-secondary-600" />}
              title="Template Libraries"
              description="Reusable enterprise patterns"
              items={[
                'Standardized prompt templates',
                'Best practice collections',
                'Domain-specific templates'
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="link" size="xl" className="text-accent-600" />}
              title="Chain-of-Thought"
              description="Structured step-by-step reasoning"
              items={[
                'Decompose complex problems',
                'Traceable reasoning',
                'Validatable intermediate steps'
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="tasks" size="xl" className="text-green-600" />}
              title="Requirement Tracing"
              description="Traceable requirement chains"
              items={[
                'Requirements traceability',
                'Change management',
                'Impact analysis'
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="check-circle" size="xl" className="text-secondary-600" />}
              title="Output Validation"
              description="Systematic quality control"
              items={[
                'Automated quality gates',
                'Peer review processes',
                'Compliance checking'
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="chart-bar" size="xl" className="text-orange-600" />}
              title="Performance Metrics"
              description="Measurable success KPIs"
              items={[
                'Response quality scores',
                'Time-to-solution metrics',
                'User satisfaction index'
              ]}
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 11: Context Engineering Setup - Integration Possibilities
    // ============================================
    {
      id: 11,
      component: () => (
        <EnhancedContentSlide
          title="� Context Engineering Integration Possibilities"
          subtitle="How would you set up Context Engineering in your team?"
          background="secondary"
        >
          <div className="space-y-8">
            <InfoBox variant="tip">
              <strong>💡 Team Activity:</strong> Discuss these questions together with your team to find the best integration approach for your organization!
            </InfoBox>

            {/* Question Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FeatureCard
                icon={<IconSolid name="question-circle" size="2xl" className="text-yellow-500" />}
                title="Question 1"
                description="Which specialized agents do you need?"
                items={[
                  'Developers? (Frontend, Backend, Full-Stack)',
                  'Architects? Reviewers?',
                  'DevOps? Security?',
                  'Domain Experts?'
                ]}
                variant="elevated"
              />
              <FeatureCard
                icon={<IconSolid name="question-circle" size="2xl" className="text-blue-500" />}
                title="Question 2"
                description="Which workflows are critical for you?"
                items={[
                  'Git strategy?',
                  'Testing approach?',
                  'Code review process?',
                  'Deployment workflow?'
                ]}
                variant="elevated"
              />
              <FeatureCard
                icon={<IconSolid name="question-circle" size="2xl" className="text-green-500" />}
                title="Question 3"
                description="How do you organize your tasks?"
                items={[
                  'Sprint planning?',
                  'Task tracking?',
                  'Documentation?',
                  'Knowledge sharing?'
                ]}
                variant="elevated"
              />
              <FeatureCard
                icon={<IconSolid name="question-circle" size="2xl" className="text-purple-500" />}
                title="Question 4"
                description="What are your biggest challenges?"
                items={[
                  'Team communication?',
                  'Code quality?',
                  'Time management?',
                  'Knowledge loss?'
                ]}
                variant="elevated"
              />
            </div>

            <InfoBox variant="success">
              <strong>💡 Next Step:</strong> Note your thoughts and discuss with your team - in the next slide I'll show you concrete implementation solutions!
            </InfoBox>
          </div>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 12: Context Engineering Setup - Implementation Guide
    // ============================================
    {
      id: 12,
      component: function ContextEngineeringSetupSlide() {
        const [selectedPrompt, setSelectedPrompt] = React.useState<{title: string; prompt: string; description: string; tags: string[]} | null>(null)
        
        return (
          <>
            <EnhancedContentSlide
              title="🚀 Context Engineering Setup"
              subtitle="Click each card for ready-to-use prompt templates"
              background="light"
            >
              <div className="space-y-8">
                {/* Step 1: Specialized Agents */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-600 text-white text-sm">1</span>
                    Set up Specialized Agents
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <button
                      onClick={() => setSelectedPrompt(contextEngineeringPrompts.agents.architect)}
                      className="group"
                    >
                      <FeatureCard
                        icon={<IconSolid name="drafting-compass" size="xl" className="text-primary-600" />}
                        title="Architect"
                        description="System Design & Architecture"
                        variant="elevated"
                      />
                    </button>
                    <button
                      onClick={() => setSelectedPrompt(contextEngineeringPrompts.agents.frontend)}
                      className="group"
                    >
                      <FeatureCard
                        icon={<IconSolid name="palette" size="xl" className="text-secondary-600" />}
                        title="Frontend Dev"
                        description="React & TypeScript"
                        variant="elevated"
                      />
                    </button>
                    <button
                      onClick={() => setSelectedPrompt(contextEngineeringPrompts.agents.backend)}
                      className="group"
                    >
                      <FeatureCard
                        icon={<IconSolid name="server" size="xl" className="text-accent-600" />}
                        title="Backend Dev"
                        description="APIs & Databases"
                        variant="elevated"
                      />
                    </button>
                    <button
                      onClick={() => setSelectedPrompt(contextEngineeringPrompts.agents.qa)}
                      className="group"
                    >
                      <FeatureCard
                        icon={<IconSolid name="clipboard-check" size="xl" className="text-green-600" />}
                        title="QA & Review"
                        description="Testing & Quality"
                        variant="elevated"
                      />
                    </button>
                  </div>
                </div>

                {/* Step 2: Workflows */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-secondary-600 text-white text-sm">2</span>
                    Define Professional Workflows
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <button
                      onClick={() => setSelectedPrompt(contextEngineeringPrompts.workflows.git)}
                      className="group"
                    >
                      <FeatureCard
                        icon={<IconSolid name="code-branch" size="xl" className="text-orange-600" />}
                        title="Git Workflow"
                        description="Branching & Commits"
                        variant="elevated"
                      />
                    </button>
                    <button
                      onClick={() => setSelectedPrompt(contextEngineeringPrompts.workflows.tdd)}
                      className="group"
                    >
                      <FeatureCard
                        icon={<IconSolid name="vial" size="xl" className="text-green-600" />}
                        title="TDD"
                        description="Test-Driven Development"
                        variant="elevated"
                      />
                    </button>
                    <button
                      onClick={() => setSelectedPrompt(contextEngineeringPrompts.workflows.specDriven)}
                      className="group"
                    >
                      <FeatureCard
                        icon={<IconSolid name="file-alt" size="xl" className="text-blue-600" />}
                        title="Spec-Driven"
                        description="Specification First"
                        variant="elevated"
                      />
                    </button>
                    <button
                      onClick={() => setSelectedPrompt(contextEngineeringPrompts.workflows.reviewing)}
                      className="group"
                    >
                      <FeatureCard
                        icon={<IconSolid name="search" size="xl" className="text-purple-600" />}
                        title="Code Review"
                        description="Review Best Practices"
                        variant="elevated"
                      />
                    </button>
                  </div>
                </div>

                {/* Step 3: Task Management & Orchestration */}
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-accent-600 text-white text-sm">3</span>
                    Task Management & Orchestration
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setSelectedPrompt(contextEngineeringPrompts.taskManagement)}
                      className="group"
                    >
                      <FeatureCard
                        icon={<IconSolid name="tasks" size="xl" className="text-primary-600" />}
                        title="Task Management"
                        description="AI-powered task tracking"
                        variant="elevated"
                      />
                    </button>
                    <button
                      onClick={() => setSelectedPrompt(contextEngineeringPrompts.masterAgent)}
                      className="group"
                    >
                      <FeatureCard
                        icon={<IconSolid name="sitemap" size="xl" className="text-secondary-600" />}
                        title="Master Orchestrator"
                        description="Agent coordination"
                        variant="elevated"
                      />
                    </button>
                  </div>
                </div>

                <div className="flex justify-center my-8">
                  <a
                    href="/context-engineering-setup.zip"
                    download="context-engineering-setup.zip"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                  >
                    <IconSolid name="download" size="xl" />
                    Download All Templates (ZIP)
                  </a>
                </div>

                <InfoBox variant="success">
                  <strong>✨ Ready to use:</strong> All templates are pre-configured and can be copied directly into your GitHub repository!
                </InfoBox>
              </div>
            </EnhancedContentSlide>

            {/* Prompt Modal */}
            {selectedPrompt && (
              <PromptModal
                isOpen={true}
                onClose={() => setSelectedPrompt(null)}
                title={selectedPrompt.title}
                description={selectedPrompt.description}
                prompt={selectedPrompt.prompt}
                tags={selectedPrompt.tags}
              />
            )}
          </>
        )
      },
      transition: 'slide',
    },

    // ============================================
    // SLIDE 13: Live Coding - Let's Build Something!
    // ============================================
    {
      id: 13,
      component: () => (
        <EnhancedContentSlide
          title="💻 Live Coding Time!"
          subtitle="Let's build a real application using our Context Engineering setup"
          background="secondary"
        >
          <div className="space-y-8">
            <InfoBox variant="tip">
              <strong>🤔 Think with me:</strong> What small, real-world application should we build together?
            </InfoBox>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <FeatureCard
                icon={<IconSolid name="lightbulb" size="2xl" className="text-yellow-500" />}
                title="Good Example Characteristics"
                description="What makes a great live coding demo?"
                items={[
                  '✅ Realistic business value',
                  '✅ 15-20 minutes to build',
                  '✅ Uses multiple agents',
                  '✅ Shows real workflows',
                  '✅ Demonstrates quality'
                ]}
                variant="elevated"
              />

              <FeatureCard
                icon={<IconSolid name="code" size="2xl" className="text-blue-500" />}
                title="Technical Scope Ideas"
                description="Perfect scope for live demo"
                items={[
                  '🎯 Simple CRUD app',
                  '🎯 API with 2-3 endpoints',
                  '🎯 Basic UI (3-5 components)',
                  '🎯 Database schema',
                  '🎯 Authentication flow'
                ]}
                variant="elevated"
              />

              <FeatureCard
                icon={<IconSolid name="rocket" size="2xl" className="text-green-500" />}
                title="Example App Ideas"
                description="Real-world mini applications"
                items={[
                  '📝 Task/Todo Manager',
                  '📚 Book Library System',
                  '🛒 Product Catalog',
                  '👥 Contact Manager',
                  '📊 Expense Tracker'
                ]}
                variant="elevated"
              />
            </div>

            <div className="bg-gradient-to-r from-primary-500/20 to-secondary-500/20 border-2 border-primary-500/50 rounded-xl p-6">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <IconSolid name="bullseye" size="xl" className="text-primary-600" />
                Perfect Demo Requirements
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-lg">
                <div className="flex items-start gap-3">
                  <IconSolid name="layer-group" size="lg" className="text-secondary-600 mt-1" />
                  <div>
                    <strong>Full Stack:</strong> Frontend (React) + Backend (Node/Express) + Database (PostgreSQL/SQLite)
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <IconSolid name="users" size="lg" className="text-secondary-600 mt-1" />
                  <div>
                    <strong>Multiple Agents:</strong> Architect, Frontend Dev, Backend Dev, QA working together
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <IconSolid name="tasks" size="lg" className="text-secondary-600 mt-1" />
                  <div>
                    <strong>Real Workflows:</strong> Git strategy, TDD, Code reviews, Deployment
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <IconSolid name="shield-check" size="lg" className="text-secondary-600 mt-1" />
                  <div>
                    <strong>Enterprise Quality:</strong> Type safety, error handling, testing, documentation
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <InfoBox variant="success">
                <strong>💡 Audience Input:</strong> Shout out your ideas! What would you like to see built?
              </InfoBox>
            </div>
          </div>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 14: What's Next?
    // ============================================
    {
      id: 14,
      component: () => (
        <EnhancedContentSlide
          title="What's Next? 🚀"
          subtitle="Taking Context Engineering Further"
          background="primary"
        >
          <div className="space-y-8">
            <div className="text-center mb-8">
              <div className="text-2xl font-light text-white/90 mb-6">
                You now have the <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent font-semibold">foundation</span> for professional AI development
              </div>
            </div>

            <div className="flex justify-center items-center gap-6 mb-8 flex-wrap">
              <FeatureCard
                icon={<IconSolid name="brain" size="xl" className="text-white" />}
                title="Context Engineering"
                description="Professional foundation ✅"
                variant="glass"
              />
              <IconSolid name="arrow-right" size="xl" className="text-white/60 hidden md:block" />
              <FeatureCard
                icon={<IconSolid name="robot" size="xl" className="text-white" />}
                title="Specialized Agents"
                description="Ready to use ✅"
                variant="glass"
              />
              <IconSolid name="arrow-right" size="xl" className="text-white/60 hidden md:block" />
              <FeatureCard
                icon={<IconSolid name="rocket" size="xl" className="text-white" />}
                title="Live Coding"
                description="Let's build! 🎯"
                variant="glass"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-3">
                  <IconSolid name="lightbulb" size="xl" className="text-yellow-400" />
                  Future: BMAD Method
                </h3>
                <p className="text-lg text-white/90 mb-3">
                  Advanced AI development methodology:
                </p>
                <ul className="text-base text-white/80 space-y-2 ml-6">
                  <li>• Advanced multi-agent workflows</li>
                  <li>• Complete project implementations</li>
                  <li>• Enterprise-grade automation</li>
                  <li>• From requirement to deployment in no time</li>
                </ul>
              </div>

              <div className="bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-3">
                  <IconSolid name="plug" size="xl" className="text-green-400" />
                  Future: MCP Servers
                </h3>
                <p className="text-lg text-white/90 mb-3">
                  Model Context Protocol integration:
                </p>
                <ul className="text-base text-white/80 space-y-2 ml-6">
                  <li>• Connect AI to external tools & data</li>
                  <li>• Custom context providers</li>
                  <li>• Real-time information access</li>
                  <li>• Extend AI capabilities dynamically</li>
                </ul>
              </div>
            </div>

            <InfoBox variant="success">
              <strong>For Now:</strong> Let's put Context Engineering into practice with a live coding session!
            </InfoBox>
          </div>
        </EnhancedContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 15: Thank You
    // ============================================
    {
      id: 15,
      component: () => (
        <EnhancedContentSlide
          title="Thank You! 🙏"
          subtitle="Questions? Let's discuss!"
          background="primary"
        >
          <div className="space-y-8">
            <div className="text-center space-y-6">
              <div className="flex justify-center gap-8 mb-8">
                <FeatureCard
                  icon={<IconSolid name="envelope" size="2xl" className="text-white" />}
                  title="Get in Touch"
                  description="Let's connect and collaborate"
                  variant="glass"
                />
                <FeatureCard
                  icon={<IconSolid name="code-branch" size="2xl" className="text-white" />}
                  title="Follow the Journey"
                  description="Stay updated on GitHub"
                  variant="glass"
                />
                <FeatureCard
                  icon={<IconSolid name="comments" size="2xl" className="text-white" />}
                  title="Share Your Experience"
                  description="Let's learn together"
                  variant="glass"
                />
              </div>

              <div className="bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-2xl p-8 mx-auto max-w-4xl">
                <div className="mb-6">
                  <IconSolid name="quote-left" size="2xl" className="text-white/60 mb-4" />
                </div>
                <blockquote className="text-2xl md:text-3xl font-light text-white/95 italic leading-relaxed mb-6">
                  "The future of programming is not about writing more code, but about writing better context. AI doesn't replace developers—it amplifies those who master the art of communication."
                </blockquote>
                <div className="flex items-center justify-center gap-3 text-white/80">
                  <IconSolid name="user-circle" size="lg" />
                  <cite className="text-lg not-italic">
                    — Andrej Karpathy, AI Researcher & Former Director of AI at Tesla
                  </cite>
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-white/20">
                <h3 className="text-3xl font-bold text-white mb-4">
                  Ready to Transform Your Development Workflow?
                </h3>
                <p className="text-xl text-white/90 mb-6">
                  Start your Context Engineering journey today!
                </p>
                <div className="flex justify-center gap-6 flex-wrap">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3 flex items-center gap-3">
                    <IconSolid name="rocket" size="lg" className="text-green-400" />
                    <span className="text-white font-semibold">Start Small</span>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3 flex items-center gap-3">
                    <IconSolid name="sync-alt" size="lg" className="text-blue-400" />
                    <span className="text-white font-semibold">Iterate Often</span>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3 flex items-center gap-3">
                    <IconSolid name="chart-line" size="lg" className="text-yellow-400" />
                    <span className="text-white font-semibold">Measure Results</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </EnhancedContentSlide>
      ),
      transition: 'fade',
    },
  ],
}